import React, { useEffect, useMemo, useState } from 'react';
import type {
  LpBlock,
  LpBlockField,
  LpPreviewMeta,
  LpPreviewPerformance,
  LpRightTab,
  LpTestingDetail,
  LpVariant,
} from '../../growth-landing-pages/types';
import {
  LP_BLOCKS,
  LP_BLOCK_CATALOG,
  LP_CREATE_META,
  LP_CREATE_PERFORMANCE,
  LP_CREATE_TESTING,
  LP_PREVIEW_META,
  LP_PREVIEW_PERFORMANCE,
  LP_TESTING,
  createLpBlockFromCatalog,
} from '../../growth-landing-pages/mockData';
import { GROWTH_LANDING_PAGES_META, LP_PHASE_META, LP_PHASE_ORDER, LP_STATUS_META } from '../../growth-landing-pages/config';
import { Icon } from '../Icon/Icon';
import { SwitchControl } from '../Toggle/Toggle';
import { LP_TONE } from './LandingPagesView';
import './growth-landing-pages.css';

const fieldInputClass =
  'w-full rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-2 text-[13px] text-[var(--ink-1)] outline-none transition focus:border-[color-mix(in_srgb,var(--success)_45%,transparent)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--success)_12%,transparent)]';

function cloneBlocks(blocks: LpBlock[]): LpBlock[] {
  return blocks.map((block) => ({
    ...block,
    fields: block.fields.map((field) => ({ ...field })),
  }));
}

const eyebrowClass = 'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function LpBadge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'success' | 'info' | 'neutral' | 'lav';
}) {
  const t = LP_TONE[tone];
  return (
    <span
      className="inline-flex items-center rounded-[var(--r-pill)] px-2 py-0.5 text-[10.5px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
    >
      {children}
    </span>
  );
}

/* ── Left rail: block stack ─────────────────────────────── */

function LpBlockStackItem({
  block,
  index,
  active,
  onClick,
}: {
  block: LpBlock;
  index: number;
  active: boolean;
  onClick: () => void;
}) {
  const tested = block.fields.some((field) => field.variesAcrossVariants);
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer rounded-[var(--r-md)] border bg-[var(--cream-1)] px-3.5 py-3 text-left transition"
      style={{
        borderColor: active ? 'var(--success)' : 'var(--cream-2)',
        background: active ? 'var(--success-tint)' : 'var(--cream-1)',
        boxShadow: active ? '0 0 0 3px color-mix(in srgb, var(--success) 12%, transparent)' : undefined,
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-[6px] font-[var(--font-mono)] text-[11px] font-semibold"
          style={{
            background: active ? 'var(--success)' : 'var(--surface-mute)',
            color: active ? 'var(--cream-1)' : 'var(--ink-2)',
          }}
        >
          {index + 1}
        </span>
        <span className="flex-1 text-[12px] font-semibold leading-[1.3] text-[var(--ink-0)]">{block.type}</span>
      </div>
      <div className="ml-8 mt-1 text-[11px] text-[var(--ink-3)]">
        {block.variant}
        {tested ? <span className="ml-1.5 font-medium text-[var(--info)]">· Tested</span> : null}
      </div>
    </button>
  );
}

/* ── Center: live preview ───────────────────────────────── */

function LpPreviewBlock({
  id,
  active,
  children,
}: {
  id: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative"
      style={{
        outline: active ? '2px solid var(--success)' : '2px solid transparent',
        outlineOffset: -2,
      }}
    >
      {active ? (
        <span className="absolute left-2 top-[-11px] z-[2] rounded-[4px] bg-[var(--success)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--cream-1)]">
          Editing
        </span>
      ) : null}
      {children}
    </div>
  );
}

function fieldVal(block: LpBlock, key: string, fallback = ''): string {
  const field = block.fields.find((row) => row.key === key);
  if (field == null || field.value == null || field.value === '') return fallback;
  return String(field.value);
}

function fieldBool(block: LpBlock, key: string): boolean {
  const field = block.fields.find((row) => row.key === key);
  return Boolean(field?.value);
}

function fieldNum(block: LpBlock, key: string, fallback = 0): number {
  const field = block.fields.find((row) => row.key === key);
  const n = Number(field?.value);
  return Number.isFinite(n) ? n : fallback;
}

function blockKind(block: LpBlock): string {
  const type = block.type.toLowerCase();
  if (type.includes('header')) return 'header';
  if (type.includes('hero')) return 'hero';
  if (type.includes('trust')) return 'trust';
  if (type.includes('transform')) return 'transform';
  if (type.includes('doctor')) return 'doctor';
  if (type.includes('financ')) return 'financing';
  if (type.includes('faq')) return 'faq';
  if (type.includes('booking')) return 'booking';
  if (type.includes('footer')) return 'footer';
  return 'generic';
}

function LpRenderPreview({
  blocks,
  activeBlockId,
  pageUrl,
  emptyLabel,
}: {
  blocks: LpBlock[];
  activeBlockId: string;
  pageUrl: string;
  emptyLabel: string;
}) {
  return (
    <div className="overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
      <div className="flex items-center gap-2.5 border-b border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--terracotta)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--gold)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--success)]" />
        </div>
        <div className="flex-1 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3 py-1.5 font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">
          {pageUrl && pageUrl !== '—' ? pageUrl : 'preview'}
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="flex min-h-[320px] items-center justify-center px-8 py-16 text-center text-[13px] leading-[1.6] text-[var(--ink-3)]">
          {emptyLabel}
        </div>
      ) : (
        blocks.map((block) => {
          const kind = blockKind(block);
          const active = activeBlockId === block.id;

          if (kind === 'header') {
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div className="flex items-center justify-between border-b border-[var(--cream-2)] px-7 py-4">
                  <div className="font-[var(--font-display)] text-[18px] font-semibold text-[var(--ink-0)]">
                    {fieldVal(block, 'logoText', 'Logo')}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-[13px] text-[var(--ink-2)]">
                      <Icon kind="phone" size={13} stroke={1.9} />
                      {fieldVal(block, 'phoneNumber', 'Phone')}
                    </span>
                    <span
                      className="rounded-[var(--r-sm)] px-4 py-2 text-[13px] font-semibold text-[var(--cream-1)]"
                      style={{ background: '#7A5CFF' }}
                    >
                      {fieldVal(block, 'ctaLabel', 'CTA')}
                    </span>
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'hero') {
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div
                  className="px-7 py-16 text-center"
                  style={{ background: 'linear-gradient(135deg, rgba(122,79,214,0.10), var(--cream-1))' }}
                >
                  <h1 className="m-0 mb-3.5 font-[var(--font-display)] text-[clamp(26px,3.4vw,38px)] font-semibold leading-[1.15] tracking-[-0.03em] text-[var(--ink-0)]">
                    {fieldVal(block, 'headline', 'Headline')}
                  </h1>
                  <p className="mx-auto mb-7 max-w-[620px] text-[17px] leading-[1.5] text-[var(--ink-2)]">
                    {fieldVal(block, 'subhead', 'Subheadline')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2.5">
                    <span
                      className="rounded-[var(--r-sm)] px-7 py-3.5 text-[15px] font-semibold text-[var(--cream-1)]"
                      style={{ background: '#7A5CFF' }}
                    >
                      {fieldVal(block, 'primaryCTA', 'Primary CTA')}
                    </span>
                    <span className="rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-7 py-3.5 text-[15px] font-medium text-[var(--ink-2)]">
                      {fieldVal(block, 'secondaryCTA', 'Secondary CTA')}
                    </span>
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'trust') {
            const badges = [
              fieldBool(block, 'showADA') ? 'ADA Member' : null,
              fieldBool(block, 'showInvisalign') ? 'Invisalign' : null,
              fieldBool(block, 'showStraumann') ? 'Straumann Partner' : null,
              fieldVal(block, 'yearsPracticing') || null,
            ].filter(Boolean) as string[];
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 bg-[var(--surface-mute)] px-7 py-5.5">
                  {(badges.length ? badges : ['Trust badges']).map((badge) => (
                    <div
                      key={badge}
                      className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--ink-2)]"
                    >
                      <Icon kind="shield-check" size={15} stroke={1.9} style={{ color: '#7A5CFF' }} />
                      {badge}
                    </div>
                  ))}
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'transform') {
            const count = Math.min(6, Math.max(1, fieldNum(block, 'caseCount', 3)));
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div className="px-7 py-14">
                  <h2 className="m-0 mb-2.5 text-center font-[var(--font-display)] text-[28px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                    Real Patient Results
                  </h2>
                  <p className="m-0 mb-7 text-center text-[14px] text-[var(--ink-3)]">
                    Filter: {fieldVal(block, 'caseFilter', 'all')} · {count} cases
                  </p>
                  <div className="mx-auto grid max-w-[780px] grid-cols-3 gap-3.5">
                    {Array.from({ length: count }, (_, index) => (
                      <div
                        key={index}
                        className="relative aspect-square overflow-hidden rounded-[var(--r-md)] bg-[var(--surface-mute)]"
                      >
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              'linear-gradient(90deg, var(--cream-2) 50%, rgba(122,79,214,0.18) 50%)',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'doctor') {
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div
                  className="mx-auto flex max-w-[900px] flex-wrap items-center gap-8 px-7 py-14"
                  style={{ background: 'linear-gradient(180deg, var(--surface-mute), var(--cream-1))' }}
                >
                  <div className="flex h-[240px] w-[200px] shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--cream-2)] text-[var(--ink-3)]">
                    <Icon kind="patients" size={48} stroke={1.4} />
                  </div>
                  <div className="min-w-[240px] flex-1">
                    <div
                      className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.06em]"
                      style={{ color: '#7A5CFF' }}
                    >
                      Meet your specialist
                    </div>
                    <h2 className="m-0 mb-1 font-[var(--font-display)] text-[26px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                      {fieldVal(block, 'doctorName', 'Doctor name')}
                    </h2>
                    <div className="mb-4 text-[13px] text-[var(--ink-3)]">
                      {fieldVal(block, 'credentials', 'Credentials')}
                    </div>
                    <div className="mb-4 flex gap-6">
                      <div>
                        <div className="font-[var(--font-display)] text-[26px] font-semibold text-[var(--ink-0)]">
                          {fieldVal(block, 'yearsExp', '0')}
                        </div>
                        <div className="text-[12px] text-[var(--ink-3)]">Years experience</div>
                      </div>
                      <div>
                        <div className="font-[var(--font-display)] text-[26px] font-semibold text-[var(--ink-0)]">
                          {fieldVal(block, 'implantCount', '0')}
                        </div>
                        <div className="text-[12px] text-[var(--ink-3)]">Implants placed</div>
                      </div>
                    </div>
                    <p className="m-0 text-[14px] leading-[1.6] text-[var(--ink-2)]">
                      {fieldVal(block, 'bio', 'Bio')}
                    </p>
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'financing') {
            const providers = [
              fieldBool(block, 'showCareCredit') ? 'CareCredit' : null,
              fieldBool(block, 'showCherry') ? 'Cherry' : null,
              fieldBool(block, 'showInHouse') ? 'In-House' : null,
            ].filter(Boolean) as string[];
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div className="bg-[var(--surface-mute)] px-7 py-14">
                  <div className="mx-auto max-w-[640px] text-center">
                    <h2 className="m-0 mb-2.5 font-[var(--font-display)] text-[28px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                      Flexible Financing
                    </h2>
                    <p className="m-0 mb-7 text-[14px] text-[var(--ink-3)]">
                      From ${fieldVal(block, 'startPrice', '0')} – ${fieldVal(block, 'endPrice', '0')}
                    </p>
                    <div className="mt-4 flex justify-center gap-2">
                      {(providers.length ? providers : ['Financing']).map((provider) => (
                        <LpBadge key={provider} tone="lav">
                          {provider}
                        </LpBadge>
                      ))}
                    </div>
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'faq') {
            const count = Math.min(8, Math.max(1, fieldNum(block, 'questionCount', 4)));
            const topics = fieldVal(block, 'topics', 'topic')
              .split(',')
              .map((topic) => topic.trim())
              .filter(Boolean);
            const questions = Array.from({ length: count }, (_, index) =>
              topics[index % Math.max(topics.length, 1)]
                ? `About ${topics[index % topics.length]}`
                : `Question ${index + 1}`,
            );
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div className="mx-auto max-w-[720px] px-7 py-14">
                  <h2 className="m-0 mb-6 text-center font-[var(--font-display)] text-[28px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                    Common Questions
                  </h2>
                  {questions.map((question, index) => (
                    <div
                      key={`${question}-${String(index)}`}
                      className="flex items-center justify-between py-4"
                      style={{
                        borderBottom:
                          index < questions.length - 1 ? '1px solid var(--cream-2)' : undefined,
                      }}
                    >
                      <span className="text-[15px] font-medium text-[var(--ink-1)]">{question}</span>
                      <Icon kind="down" size={16} stroke={2} className="text-[var(--ink-4)]" />
                    </div>
                  ))}
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'booking') {
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div
                  className="px-7 py-14"
                  style={{
                    background:
                      'linear-gradient(135deg, color-mix(in srgb, #7A5CFF 88%, black), color-mix(in srgb, #7A5CFF 70%, black))',
                  }}
                >
                  <div className="mx-auto max-w-[520px] rounded-[var(--r-lg)] bg-[var(--cream-1)] px-8 py-9 shadow-[var(--sh-2)]">
                    <h2 className="m-0 mb-5 font-[var(--font-display)] text-[24px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
                      Booking
                    </h2>
                    <div className="grid gap-2.5">
                      {['Your Name', 'Phone Number', 'Email']
                        .concat(fieldBool(block, 'showInsuranceDropdown') ? ['Insurance Provider'] : [])
                        .map((field) => (
                          <div
                            key={field}
                            className="rounded-[var(--r-sm)] border border-[var(--cream-2)] px-3.5 py-2.5 text-[13px] text-[var(--ink-4)]"
                          >
                            {field}
                          </div>
                        ))}
                      <span
                        className="mt-1 rounded-[var(--r-sm)] py-3.5 text-center text-[14px] font-semibold text-[var(--cream-1)]"
                        style={{ background: '#7A5CFF' }}
                      >
                        {fieldVal(block, 'primaryCTA', 'Submit')}
                      </span>
                    </div>
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          if (kind === 'footer') {
            return (
              <LpPreviewBlock key={block.id} id={block.id} active={active}>
                <div
                  className="px-7 py-7 text-center text-[11px]"
                  style={{ background: 'var(--grad-ink)', color: 'rgba(255,255,255,0.7)' }}
                >
                  {fieldBool(block, 'showNAP') ? (
                    <div className="mb-1.5 text-[12px]">NAP block enabled</div>
                  ) : null}
                  <div className="opacity-70">
                    {fieldVal(block, 'complianceText', 'Compliance text')}
                  </div>
                </div>
              </LpPreviewBlock>
            );
          }

          return (
            <LpPreviewBlock key={block.id} id={block.id} active={active}>
              <div className="border-b border-[var(--cream-2)] px-7 py-8">
                <div className={`${eyebrowClass} mb-2`}>{block.type}</div>
                <div className="space-y-1.5">
                  {block.fields
                    .filter((field) => field.type !== 'info')
                    .map((field) => (
                      <div key={field.key} className="text-[13px] text-[var(--ink-2)]">
                        <span className="font-medium text-[var(--ink-3)]">{field.label}: </span>
                        {String(field.value ?? '') || '—'}
                      </div>
                    ))}
                </div>
              </div>
            </LpPreviewBlock>
          );
        })
      )}
    </div>
  );
}

/* ── Right panel: block editor ──────────────────────────── */

function LpField({
  field,
  onChange,
  disabled,
}: {
  field: LpBlockField;
  onChange?: (value: string | number | boolean) => void;
  disabled?: boolean;
}) {
  if (field.type === 'info') {
    return (
      <div className="mb-3.5 rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--info)_30%,transparent)] bg-[var(--info-tint)] px-3 py-2.5">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[var(--info)]">{field.label}</div>
        <div className="text-[12px] leading-[1.5] text-[var(--ink-2)]">{String(field.value)}</div>
      </div>
    );
  }

  const readOnly = !onChange || Boolean(disabled);

  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-[var(--ink-2)]">
          {field.label}
          {field.variesAcrossVariants ? (
            <span className="rounded-[4px] bg-[var(--info-tint)] px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.03em] text-[var(--info)]">
              Tested
            </span>
          ) : null}
        </span>
        {field.maxLen ? (
          <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">
            {String(field.value).length} / {field.maxLen}
          </span>
        ) : null}
      </div>

      {field.type === 'boolean' ? (
        <div className="flex items-center gap-2.5 py-1">
          <SwitchControl
            size="sm"
            checked={Boolean(field.value)}
            disabled={readOnly}
            onChange={(checked) => onChange?.(checked)}
          />
          <span className="text-[12px] text-[var(--ink-3)]">{field.value ? 'Enabled' : 'Disabled'}</span>
        </div>
      ) : field.type === 'textarea' ? (
        <textarea
          value={String(field.value)}
          disabled={readOnly}
          maxLength={field.maxLen}
          rows={4}
          onChange={(e) => onChange?.(e.target.value)}
          className={`${fieldInputClass} min-h-[88px] resize-y leading-[1.55]`}
        />
      ) : field.type === 'select' && field.options?.length ? (
        <select
          value={String(field.value)}
          disabled={readOnly}
          onChange={(e) => onChange?.(e.target.value)}
          className={fieldInputClass}
        >
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : field.type === 'number' ? (
        <input
          type="number"
          value={typeof field.value === 'number' ? field.value : Number(field.value) || 0}
          disabled={readOnly}
          onChange={(e) => onChange?.(Number(e.target.value))}
          className={fieldInputClass}
        />
      ) : (
        <input
          type="text"
          value={String(field.value)}
          disabled={readOnly}
          maxLength={field.maxLen}
          onChange={(e) => onChange?.(e.target.value)}
          className={fieldInputClass}
        />
      )}

      {field.note ? (
        <div className="mt-1.5 flex gap-1.5 text-[11px] leading-[1.5] text-[var(--ink-3)]">
          <Icon kind="info" size={11} stroke={2} className="mt-0.5 shrink-0 text-[var(--ink-4)]" />
          <span>{field.note}</span>
        </div>
      ) : null}
    </div>
  );
}

function LpBlocksTab({
  block,
  onFieldChange,
  onApply,
  onRevert,
  saving = false,
  dirty = false,
  canSave = true,
  applyLabel = 'Apply Changes',
  allowApplyWithoutDirty = false,
}: {
  block: LpBlock;
  onFieldChange?: (fieldKey: string, value: string | number | boolean) => void;
  onApply?: () => void;
  onRevert?: () => void;
  saving?: boolean;
  dirty?: boolean;
  canSave?: boolean;
  applyLabel?: string;
  allowApplyWithoutDirty?: boolean;
}) {
  const applyEnabled = canSave && Boolean(onApply) && !saving && (dirty || allowApplyWithoutDirty);
  return (
    <>
      <div className="border-b border-[var(--cream-2)] px-5 py-5">
        <div className={`${eyebrowClass} mb-1`}>Block Editor</div>
        <div className="mb-1 font-[var(--font-display)] text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          {block.type}
        </div>
        <div className="text-[12px] text-[var(--ink-3)]">
          Variant: <span className="font-semibold text-[var(--ink-1)]">{block.variant}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {block.fields.map((field) => (
          <LpField
            key={field.key}
            field={field}
            {...(onFieldChange
              ? {
                  onChange: (value) => {
                    onFieldChange(field.key, value);
                  },
                }
              : {})}
            disabled={saving}
          />
        ))}
      </div>
      <div className="flex gap-2 border-t border-[var(--cream-2)] bg-[var(--surface-mute)] px-5 py-3.5">
        <button
          type="button"
          onClick={onRevert}
          disabled={!dirty || saving || !onRevert || allowApplyWithoutDirty}
          className="flex-1 cursor-pointer rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2 text-center text-[12px] font-medium text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Revert to AI
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={!applyEnabled}
          className="flex-1 cursor-pointer rounded-[var(--r-sm)] border-0 bg-[var(--success)] px-3 py-2 text-center text-[12px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : applyLabel}
        </button>
      </div>
    </>
  );
}

/* ── Right panel: variants ──────────────────────────────── */

function LpVariantCard({ variant }: { variant: LpVariant }) {
  const crColor = variant.cr > 6 ? 'var(--success)' : variant.cr > 4 ? 'var(--ink-2)' : 'var(--ink-3)';
  return (
    <div
      className="mb-2.5 rounded-[var(--r-md)] border bg-[var(--cream-1)] px-4 py-3.5"
      style={{
        borderColor: variant.isWinner ? 'color-mix(in srgb, var(--success) 50%, transparent)' : 'var(--cream-2)',
        boxShadow: variant.isWinner
          ? '0 0 0 3px color-mix(in srgb, var(--success) 15%, transparent)'
          : 'var(--sh-1)',
      }}
    >
      <div className="mb-2.5 flex items-center gap-2">
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] font-[var(--font-mono)] text-[12px] font-bold"
          style={{
            background: variant.isWinner
              ? 'var(--success)'
              : variant.status === 'active'
                ? 'var(--info)'
                : 'var(--cream-2)',
            color: variant.status === 'active' || variant.isWinner ? 'var(--cream-1)' : 'var(--ink-2)',
          }}
        >
          {variant.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-[var(--ink-0)]">
            {variant.label}
            {variant.isWinner ? <LpBadge tone="success">★ Winner</LpBadge> : null}
            {!variant.isWinner && variant.status === 'retired' ? <LpBadge tone="neutral">Retired</LpBadge> : null}
            {variant.isOriginal ? <LpBadge tone="info">Control</LpBadge> : null}
          </div>
          <div className="mt-0.5 text-[11px] text-[var(--ink-3)]">{variant.strategy}</div>
        </div>
      </div>

      {variant.note ? (
        <div className="rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-1.5 text-[11px] italic text-[var(--ink-3)]">
          {variant.note}
        </div>
      ) : (
        <>
          <div className="mb-2.5 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-2">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ink-3)]">Headline</div>
            <div className="text-[12px] font-medium leading-[1.4] text-[var(--ink-1)]">{variant.headline}</div>
          </div>
          <div className="mb-3 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-2.5 py-2">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ink-3)]">CTA</div>
            <div className="text-[12px] font-medium text-[var(--ink-1)]">{variant.cta}</div>
          </div>
          <div className="flex gap-2.5 border-t border-[var(--cream-2)] pt-2.5">
            <div className="flex-1">
              <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ink-3)]">CR</div>
              <div className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.01em]" style={{ color: crColor }}>
                {variant.cr.toFixed(2)}%
              </div>
            </div>
            <div className="flex-1">
              <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ink-3)]">Conv</div>
              <div className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.01em] text-[var(--ink-1)]">
                {variant.conversions}
              </div>
            </div>
            <div className="flex-1">
              <div className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ink-3)]">Visits</div>
              <div className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.01em] text-[var(--ink-1)]">
                {variant.visits.toLocaleString()}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LpVariantsTab({ testing }: { testing: LpTestingDetail }) {
  return (
    <>
      <div className="border-b border-[var(--cream-2)] px-5 py-5">
        <div className={`${eyebrowClass} mb-1`}>Tested Variants</div>
        <div className="font-[var(--font-display)] text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          All {testing.totalVariants} variants
        </div>
        <div className="mt-1 text-[12px] text-[var(--ink-3)]">
          {testing.winner ? (
            <>
              Winner: <span className="font-semibold text-[var(--success)]">{testing.winner}</span>
            </>
          ) : (
            'Variant performance from live A/B test'
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {testing.variants.map((variant) => (
          <LpVariantCard key={variant.id} variant={variant} />
        ))}
      </div>
    </>
  );
}

/* ── Right panel: testing ───────────────────────────────── */

function LpPhaseStepper({ phase, budgetSpent }: { phase: LpTestingDetail['phase']; budgetSpent: number }) {
  const currentIdx = LP_PHASE_ORDER.indexOf(phase);
  return (
    <div className="rounded-[var(--r-md)] bg-[var(--surface-mute)] px-4 py-3.5">
      {LP_PHASE_ORDER.map((phaseKey, index) => {
        const phase = LP_PHASE_META[phaseKey];
        const tone = LP_TONE[phase.tone];
        const isPast = index < currentIdx;
        const isCurrent = index === currentIdx;
        return (
          <div key={phaseKey} className="relative flex items-start gap-3">
            <span
              className="z-[1] inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full font-[var(--font-mono)] text-[11px] font-bold"
              style={{
                background: isPast ? 'var(--success)' : isCurrent ? tone.tint : 'var(--cream-2)',
                border: isCurrent ? `2px solid ${tone.accent}` : 'none',
                color: isPast ? 'var(--cream-1)' : isCurrent ? tone.accent : 'var(--ink-4)',
              }}
            >
              {isPast ? <Icon kind="check" size={11} stroke={3} /> : index + 1}
            </span>
            {index < LP_PHASE_ORDER.length - 1 ? (
              <span
                className="absolute left-3 top-[26px] w-0.5"
                style={{ bottom: -16, background: isPast ? 'var(--success)' : 'var(--cream-2)' }}
              />
            ) : null}
            <div className="flex-1" style={{ paddingBottom: index < LP_PHASE_ORDER.length - 1 ? 18 : 0 }}>
              <div className="mb-0.5 flex items-center gap-1.5">
                <span
                  className="text-[13px] font-semibold"
                  style={{ color: isCurrent ? 'var(--ink-0)' : isPast ? 'var(--success)' : 'var(--ink-3)' }}
                >
                  {phase.label}
                </span>
                <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">{phase.pct}% budget</span>
                {isCurrent ? (
                  <span className="ml-auto">
                    <LpBadge tone="info">Current</LpBadge>
                  </span>
                ) : null}
              </div>
              <div className="text-[11.5px] leading-[1.5] text-[var(--ink-3)]">{phase.description}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LpTestingTab({
  testing,
  onRefreshTest,
  onDeclareWinner,
  refreshing = false,
  declaring = false,
}: {
  testing: LpTestingDetail;
  onRefreshTest?: () => void;
  onDeclareWinner?: (variantId: string) => void;
  refreshing?: boolean;
  declaring?: boolean;
}) {
  const controlVariant = testing.variants.find((variant) => variant.isOriginal);

  return (
    <>
      <div className="border-b border-[var(--cream-2)] px-5 py-5">
        <div className={`${eyebrowClass} mb-1`}>Optimization Status</div>
        <div className="font-[var(--font-display)] text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          {testing.phase === 'exploitation' || testing.phase === 'converged' ? 'Winner Running' : 'Testing Active'}
        </div>
        <div className="mt-1 text-[12px] text-[var(--ink-3)]">
          {testing.winner ? `Variant ${testing.winner} leading` : `${testing.activeVariants} active variants`}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        <div className={`${eyebrowClass} mb-2.5`}>Testing Journey</div>
        <LpPhaseStepper phase={testing.phase} budgetSpent={testing.budgetSpent} />

        <div className="mt-5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3.5">
          <div className="mb-2.5 flex justify-between">
            <span className={eyebrowClass}>Budget Spent</span>
            <span className="font-[var(--font-mono)] text-[12px] font-semibold text-[var(--ink-1)]">
              {testing.budgetSpent}%
            </span>
          </div>
          <div className="mb-2 flex h-2 overflow-hidden rounded-full bg-[var(--surface-mute)]">
            <span className="h-full bg-[var(--success)]" style={{ width: '20%', borderRight: '2px solid var(--cream-1)' }} />
            <span className="h-full bg-[var(--success)]" style={{ width: '30%', borderRight: '2px solid var(--cream-1)' }} />
            <span
              className="h-full bg-[var(--success)]"
              style={{ width: `${Math.max(0, testing.budgetSpent - 50)}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-[var(--ink-4)]">
            <span>20% Explore</span>
            <span>+30% Validate</span>
            <span>+50% Exploit</span>
          </div>
        </div>

        <div className="mt-3.5 flex items-center gap-3 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--info)_30%,transparent)] bg-[var(--info-tint)] px-4 py-3.5">
          <Icon kind="refresh" size={18} stroke={2} className="shrink-0 text-[var(--info)]" />
          <div className="text-[12px] leading-[1.5] text-[var(--ink-2)]">
            <span className="font-semibold text-[var(--ink-1)]">
              Next refresh test in {testing.nextRefreshIn}
            </span>
            <br />
            ELVA will automatically test new variants against the current winner to prevent stale pages.
          </div>
        </div>

        <div className="mt-6 border-t border-[var(--cream-2)] pt-5">
          <div className={`${eyebrowClass} mb-3`}>Manual Controls</div>
          {onRefreshTest ? (
            <button
              type="button"
              onClick={onRefreshTest}
              disabled={refreshing}
              className="mb-2 flex w-full cursor-pointer items-center gap-2.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2.5 text-[13px] font-medium text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon kind="refresh" size={14} stroke={2} />
              {refreshing ? 'Starting test round…' : 'Start new test round now'}
            </button>
          ) : (
            <div className="mb-2 flex w-full items-center gap-2.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2.5 text-[13px] font-medium text-[var(--ink-2)]">
              <Icon kind="refresh" size={14} stroke={2} />
              Start new test round now
            </div>
          )}
          {onDeclareWinner && controlVariant ? (
            <button
              type="button"
              onClick={() => onDeclareWinner(controlVariant.id)}
              disabled={declaring}
              className="mb-2 flex w-full cursor-pointer items-center gap-2.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2.5 text-[13px] font-medium text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Icon kind="left" size={14} stroke={2} />
              {declaring ? 'Reverting…' : `Revert to Variant ${controlVariant.label} (control)`}
            </button>
          ) : (
            <div className="mb-2 flex w-full items-center gap-2.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2.5 text-[13px] font-medium text-[var(--ink-2)]">
              <Icon kind="left" size={14} stroke={2} />
              Revert to Variant A (control)
            </div>
          )}
          <div
            className="mb-2 flex w-full items-center gap-2.5 rounded-[var(--r-sm)] border bg-[var(--cream-1)] px-3.5 py-2.5 text-[13px] font-medium"
            style={{ borderColor: 'color-mix(in srgb, var(--gold) 40%, transparent)', color: 'var(--gold)' }}
          >
            <Icon kind="pause" size={14} stroke={2} />
            Pause optimization (not recommended)
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Main ───────────────────────────────────────────────── */

export interface LandingPagePreviewData {
  meta?: LpPreviewMeta;
  performance?: LpPreviewPerformance;
  blocks?: LpBlock[];
  testing?: LpTestingDetail;
}

export interface LandingPagePreviewViewProps {
  draft?: LandingPagePreviewData;
  loading?: boolean;
  saving?: boolean;
  /** Same editor chrome; create hides live/pause and uses template blocks. */
  mode?: 'view' | 'create';
  onBack?: () => void;
  onViewLive?: () => void;
  onPause?: () => void;
  onApplyBlocks?: (blocks: LpBlock[], meta?: LpPreviewMeta) => void | Promise<void>;
  onRefreshTest?: () => void;
  onDeclareWinner?: (variantId: string) => void;
  refreshingTest?: boolean;
  declaringWinner?: boolean;
}

export function LandingPagePreviewView({
  draft,
  loading = false,
  saving = false,
  mode = 'view',
  onBack,
  onViewLive,
  onPause,
  onApplyBlocks,
  onRefreshTest,
  onDeclareWinner,
  refreshingTest = false,
  declaringWinner = false,
}: LandingPagePreviewViewProps = {}) {
  const isCreate = mode === 'create';
  const meta = GROWTH_LANDING_PAGES_META;
  const [createMeta, setCreateMeta] = useState(() => draft?.meta ?? LP_CREATE_META);
  const previewMeta = isCreate
    ? createMeta
    : (draft?.meta ?? LP_PREVIEW_META);
  const performance =
    draft?.performance ?? (isCreate ? LP_CREATE_PERFORMANCE : LP_PREVIEW_PERFORMANCE);
  const controlled = draft !== undefined || isCreate;
  const sourceBlocks = controlled
    ? (draft?.blocks ?? (isCreate ? [] : []))
    : LP_BLOCKS;
  const testing = draft?.testing ?? (isCreate ? LP_CREATE_TESTING : LP_TESTING);
  const statusMeta = LP_STATUS_META[previewMeta.status] ?? LP_STATUS_META.live;
  const phaseMeta = LP_PHASE_META[testing.phase] ?? LP_PHASE_META.exploitation;
  const winnerLabel = testing.winner && testing.winner !== '—' ? testing.winner : null;

  const [editableBlocks, setEditableBlocks] = useState<LpBlock[]>(() => cloneBlocks(sourceBlocks));
  const [savedBlocks, setSavedBlocks] = useState<LpBlock[]>(() => cloneBlocks(sourceBlocks));
  const [activeBlockId, setActiveBlockId] = useState<string>(sourceBlocks[0]?.id ?? '');
  const [rightTab, setRightTab] = useState<LpRightTab>('blocks');

  const blocksDirty = useMemo(
    () => JSON.stringify(editableBlocks) !== JSON.stringify(savedBlocks),
    [editableBlocks, savedBlocks],
  );

  useEffect(() => {
    if (blocksDirty) return;
    const next = cloneBlocks(sourceBlocks);
    setEditableBlocks(next);
    setSavedBlocks(cloneBlocks(sourceBlocks));
    setActiveBlockId((current) =>
      next.some((block) => block.id === current) ? current : (next[0]?.id ?? ''),
    );
  }, [sourceBlocks, blocksDirty]);

  useEffect(() => {
    if (draft?.meta && isCreate) setCreateMeta(draft.meta);
  }, [draft?.meta, isCreate]);

  const activeBlock = editableBlocks.find((block) => block.id === activeBlockId) ?? editableBlocks[0];
  const hasBlocks = editableBlocks.length > 0;
  const testedCount = editableBlocks.filter((block) =>
    block.fields.some((field) => field.variesAcrossVariants),
  ).length;

  useEffect(() => {
    if (isCreate) {
      setRightTab('blocks');
      return;
    }
    if (!hasBlocks && rightTab === 'blocks') {
      setRightTab('variants');
    }
  }, [hasBlocks, rightTab, isCreate]);

  const handleAddBlock = (type: string) => {
    const block = createLpBlockFromCatalog(
      type,
      editableBlocks.map((row) => row.id),
    );
    if (!block) return;
    setEditableBlocks((current) => [...current, block]);
    setActiveBlockId(block.id);
    setRightTab('blocks');
  };

  const handleRemoveBlock = (blockId: string) => {
    setEditableBlocks((current) => {
      const next = current.filter((block) => block.id !== blockId);
      setActiveBlockId((active) =>
        active === blockId ? (next[0]?.id ?? '') : active,
      );
      return next;
    });
  };

  const handleMoveBlock = (blockId: string, direction: -1 | 1) => {
    setEditableBlocks((current) => {
      const index = current.findIndex((block) => block.id === blockId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const tmp = next[index]!;
      next[index] = next[target]!;
      next[target] = tmp;
      return next;
    });
  };

  const handleFieldChange = (fieldKey: string, value: string | number | boolean) => {
    setEditableBlocks((current) =>
      current.map((block) =>
        block.id !== activeBlockId
          ? block
          : {
              ...block,
              fields: block.fields.map((field) =>
                field.key === fieldKey ? { ...field, value } : field,
              ),
            },
      ),
    );
  };

  const handleRevertBlock = () => {
    const saved = savedBlocks.find((block) => block.id === activeBlockId);
    if (!saved) return;
    setEditableBlocks((current) =>
      current.map((block) => (block.id === activeBlockId ? cloneBlocks([saved])[0]! : block)),
    );
  };

  const handleApplyBlocks = async () => {
    if (!onApplyBlocks) return;
    await onApplyBlocks(editableBlocks, isCreate ? createMeta : undefined);
    setSavedBlocks(cloneBlocks(editableBlocks));
  };

  const tabs: { id: LpRightTab; label: string; count?: number }[] = [
    { id: 'blocks', label: 'Blocks' },
    { id: 'variants', label: 'Variants', count: testing.totalVariants },
    { id: 'testing', label: 'Testing' },
  ];

  return (
    <div className="growth-landing-pages-root relative mx-auto w-full max-w-[1280px] px-[30px] pb-[60px] pt-[26px]">
      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="grid" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2.5">
            {isCreate ? (
              <input
                value={previewMeta.title}
                onChange={(event) =>
                  setCreateMeta((current) => ({ ...current, title: event.target.value }))
                }
                placeholder="Landing page title…"
                className="m-0 min-w-[220px] flex-1 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2 font-[var(--font-display)] text-[clamp(20px,2.4vw,26px)] font-semibold leading-[1.1] tracking-[-0.025em] text-[var(--ink-0)] outline-none focus:border-[color-mix(in_srgb,var(--success)_45%,transparent)]"
              />
            ) : (
              <h1 className="m-0 font-[var(--font-display)] text-[clamp(22px,2.6vw,28px)] font-semibold leading-[1.1] tracking-[-0.025em] text-[var(--ink-0)]">
                {previewMeta.title}
              </h1>
            )}
            <LpBadge tone="lav">{previewMeta.preset.replace(/_/g, ' ')}</LpBadge>
            <LpBadge tone={previewMeta.status === 'live' ? 'success' : 'neutral'}>
              ● {statusMeta.label}
            </LpBadge>
            {winnerLabel ? (
              <LpBadge tone="success">
                ★ {phaseMeta.label} · Variant {winnerLabel} winner
              </LpBadge>
            ) : null}
          </div>
          <div className="flex gap-2">
            {!isCreate ? (
              <>
                <button
                  type="button"
                  onClick={onViewLive}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2 text-[13px] font-medium text-[var(--ink-2)]"
                >
                  <Icon kind="external" size={13} stroke={2} />
                  View Live
                </button>
                <button
                  type="button"
                  onClick={onPause}
                  className="inline-flex cursor-pointer items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-2 text-[13px] font-medium text-[var(--ink-2)]"
                >
                  Pause Page
                </button>
              </>
            ) : (
              <span className="inline-flex items-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-3.5 py-2 text-[12.5px] font-medium text-[var(--ink-3)]">
                Compose blocks · then Create
              </span>
            )}
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2.5 font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
          <span className="inline-flex items-center gap-1">
            <Icon kind="link" size={11} stroke={2} />
            {previewMeta.url}
          </span>
          <span className="text-[var(--ink-4)]">·</span>
          <span>
            Campaign: <span className="font-[var(--font-body)] font-semibold text-[var(--ink-1)]">{previewMeta.campaign}</span>
          </span>
        </div>
      </div>

      <div className="lp-stats-bar mb-5 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-3.5 shadow-[var(--sh-1)]">
        {[
          { label: 'Visits', value: performance.visits.toLocaleString() },
          { label: 'Conversions', value: String(performance.conversions) },
          { label: 'CR', value: `${performance.conversionRate}%`, color: 'var(--success)' },
          { label: 'Forms', value: String(performance.formFills) },
          { label: 'Calls', value: String(performance.calls) },
          { label: 'Bounce', value: `${performance.bounceRate}%` },
          {
            label: 'Avg time',
            value: `${String(Math.floor(performance.avgTimeSec / 60))}m ${String(performance.avgTimeSec % 60)}s`,
          },
        ].map((metric) => (
          <div key={metric.label}>
            <div className={`${eyebrowClass} mb-0.5`}>{metric.label}</div>
            <div
              className="font-[var(--font-display)] text-[16px] font-semibold tracking-[-0.01em]"
              style={{ color: metric.color ?? 'var(--ink-0)' }}
            >
              {metric.value}
            </div>
          </div>
        ))}
      </div>

      <div className="lp-editor-grid grid gap-4" style={{ gridTemplateColumns: '230px 1fr 380px' }}>
        <div className="rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 shadow-[var(--sh-1)]">
          <div className="mb-3.5">
            <div className={`${eyebrowClass} mb-1`}>Composed Blocks</div>
            <div className="text-[11.5px] text-[var(--ink-3)]">
              {editableBlocks.length} blocks · {testedCount} tested
            </div>
          </div>

          {isCreate ? (
            <div className="mb-3 flex flex-col gap-1.5">
              <div className={`${eyebrowClass}`}>Add block</div>
              <select
                className="w-full rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-2 text-[12.5px] text-[var(--ink-1)] outline-none"
                defaultValue=""
                onChange={(event) => {
                  const type = event.target.value;
                  if (type) handleAddBlock(type);
                  event.target.value = '';
                }}
              >
                <option value="" disabled>
                  Choose a block type…
                </option>
                {LP_BLOCK_CATALOG.map((block) => (
                  <option key={block.type} value={block.type}>
                    {block.type}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            {hasBlocks ? (
              editableBlocks.map((block, index) => (
                <div key={block.id} className="flex items-start gap-1">
                  <div className="min-w-0 flex-1">
                    <LpBlockStackItem
                      block={block}
                      index={index}
                      active={activeBlockId === block.id}
                      onClick={() => {
                        setActiveBlockId(block.id);
                        setRightTab('blocks');
                      }}
                    />
                  </div>
                  {isCreate ? (
                    <div className="flex flex-col gap-0.5 pt-1">
                      <button
                        type="button"
                        onClick={() => handleMoveBlock(block.id, -1)}
                        disabled={index === 0}
                        className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)] disabled:opacity-40"
                        aria-label="Move up"
                      >
                        <Icon kind="up" size={11} stroke={2} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveBlock(block.id, 1)}
                        disabled={index === editableBlocks.length - 1}
                        className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)] disabled:opacity-40"
                        aria-label="Move down"
                      >
                        <Icon kind="down" size={11} stroke={2} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveBlock(block.id)}
                        className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--terracotta)]"
                        aria-label="Remove block"
                      >
                        <Icon kind="trash" size={11} stroke={2} />
                      </button>
                    </div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="rounded-[var(--r-md)] border border-dashed border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-5 text-center text-[12px] leading-[1.5] text-[var(--ink-3)]">
                {isCreate
                  ? 'No blocks yet. Choose a block type above to start composing.'
                  : controlled
                    ? 'No composed blocks returned yet.'
                    : 'No blocks in this view.'}
              </div>
            )}
          </div>
        </div>

        <div>
          <LpRenderPreview
            blocks={editableBlocks}
            activeBlockId={activeBlockId}
            pageUrl={previewMeta.url}
            emptyLabel={
              isCreate
                ? 'Preview is empty. Add blocks on the left to see them here.'
                : 'No blocks to preview yet.'
            }
          />
          <div className="mt-3 text-center text-[11px] text-[var(--ink-4)]">
            {isCreate
              ? 'Live preview of the blocks you add'
              : 'Live preview · edits appear after you change fields'}
          </div>
        </div>

        <div className="flex flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
          <div className="flex border-b border-[var(--cream-2)]">
            {tabs.map((tab) => {
              const active = rightTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setRightTab(tab.id);
                  }}
                  className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 border-0 bg-transparent px-3.5 py-3 text-[12.5px] font-medium"
                  style={{
                    color: active ? 'var(--success)' : 'var(--ink-3)',
                    borderBottom: active ? '2px solid var(--success)' : '2px solid transparent',
                    marginBottom: -1,
                  }}
                >
                  {tab.label}
                  {tab.count ? (
                    <span
                      className="rounded-[var(--r-pill)] px-1.5 py-0.5 text-[10.5px] font-semibold"
                      style={{
                        background: active ? 'var(--success-tint)' : 'var(--surface-mute)',
                        color: active ? 'var(--success)' : 'var(--ink-3)',
                      }}
                    >
                      {tab.count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="flex flex-1 flex-col overflow-hidden">
            {rightTab === 'blocks' ? (
              activeBlock ? (
                <LpBlocksTab
                  block={activeBlock}
                  onFieldChange={handleFieldChange}
                  onApply={() => {
                    void handleApplyBlocks();
                  }}
                  onRevert={handleRevertBlock}
                  saving={saving}
                  dirty={blocksDirty}
                  canSave={Boolean(onApplyBlocks) && (!isCreate || hasBlocks)}
                  applyLabel={isCreate ? 'Create Landing Page' : 'Apply Changes'}
                  allowApplyWithoutDirty={isCreate && hasBlocks}
                />
              ) : (
                <div className="flex flex-1 flex-col px-5 py-8 text-center text-[13px] leading-[1.6] text-[var(--ink-3)]">
                  {isCreate
                    ? 'Add a block from the left rail, then edit its fields here.'
                    : 'No blocks to edit yet. Switch to Variants or Testing, or wait for ELVA to compose this page.'}
                </div>
              )
            ) : null}
            {rightTab === 'variants' ? <LpVariantsTab testing={testing} /> : null}
            {rightTab === 'testing' ? (
              isCreate ? (
                <div className="flex flex-1 flex-col px-5 py-8 text-center text-[13px] leading-[1.6] text-[var(--ink-3)]">
                  A/B testing starts after this page is created and live.
                </div>
              ) : (
                <LpTestingTab
                  testing={testing}
                  {...(onRefreshTest ? { onRefreshTest } : {})}
                  {...(onDeclareWinner ? { onDeclareWinner } : {})}
                  refreshing={refreshingTest}
                  declaring={declaringWinner}
                />
              )
            ) : null}
          </div>
        </div>
      </div>

      {onBack ? (
        <div className="mt-6 border-t border-[var(--cream-2)] pt-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[13px] font-medium text-[var(--ink-2)]"
          >
            <Icon kind="left" size={14} stroke={2} />
            Back to Landing Pages
          </button>
        </div>
      ) : null}
    </div>
  );
}
